import { useCallback, useEffect, useRef, useState } from 'react';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { DrawSVGPlugin } from 'gsap/dist/DrawSVGPlugin';

import { observeElementResize } from '#/lib/observe-element-resize';

import { LINE_DRAW_SPEED } from '../HomePagePain/animationUtils';
import { usePauseTimelineWhenOffscreen } from '../HomePagePain/usePauseTimelineWhenOffscreen';
import {
  AGENT_LABELS,
  DECISION_BORDER_CLASS,
  DECISION_NO_COLOR,
} from './atoms';

gsap.registerPlugin(DrawSVGPlugin);

/** Every Nth loop the decision is "no": stop at the Decision API and start over. */
const SHORT_LOOP_EVERY = 3;
/** Seconds to fade the lit lines out before a loop restarts. */
const FADE_OUT = 0.3;
/** Seconds for the Decision API border to turn red on a "no". */
const NO_TURN_RED = 0.25;
/** Seconds to hold the red border before everything fades out. */
const NO_HOLD = 0.6;
/**
 * Playback rate for the whole timeline. Half the painpoint module's speed —
 * this diagram accumulates rather than pulses, so it reads better slower.
 */
const PLAYBACK_SPEED = LINE_DRAW_SPEED * 0.5;

export type BuildRailsTimeline = (args: {
  tl: gsap.core.Timeline;
  q: gsap.utils.SelectorFunc;
  /**
   * Place this with `tl.call(gate, undefined, position)` once the Decision
   * API is lit. When the decision is "no" it fades everything out and restarts
   * the timeline there instead of continuing to the endpoints.
   */
  gate: () => void;
}) => void;

/**
 * Looping GSAP timeline for the Rails Architecture diagram, mirroring the
 * homepage painpoint module: rebuilds on meaningful width changes, pauses
 * offscreen, and cycles the highlighted agent pill once per loop. Two of
 * every three loops run through to the endpoint cards; the third restarts
 * once the Decision API has been reached.
 */
export function useRailsTimeline(build: BuildRailsTimeline) {
  const timelineRef = useRef<GSAPTimeline>(null);
  const fadeRef = useRef<gsap.core.Timeline>(null);
  const container = useRef<HTMLDivElement>(null);
  const cycleRef = useRef(0);
  const [activeButton, setActiveButton] = useState(0);

  // Pauses both the timeline and the out-of-timeline "no" fade while the
  // section is offscreen, and tells us whether it's safe to start playing.
  const isVisibleRef = usePauseTimelineWhenOffscreen(
    container,
    timelineRef,
    fadeRef,
  );

  const restartAnimation = useCallback(() => {
    timelineRef.current?.kill();
    fadeRef.current?.kill();

    const q = gsap.utils.selector(container.current);

    const nextLoop = (tl: gsap.core.Timeline) => {
      cycleRef.current += 1;
      // Functional update keeps this callback stable across renders.
      setActiveButton((current) => (current + 1) % AGENT_LABELS.length);
      // restart() un-pauses, so re-apply the offscreen pause if the user
      // scrolled away while this loop was finishing.
      tl.restart();
      if (!isVisibleRef.current) tl.pause();
    };

    const tl = gsap.timeline({
      // Only start once the section is in view (see below).
      paused: true,
      onComplete: () => nextLoop(tl),
    });

    // Decision "no": hold the timeline, turn the Decision API border red so
    // it's clear the request was stopped, then fade everything out and start
    // over. Runs outside the main timeline at the same playback rate.
    const gate = () => {
      if (cycleRef.current % SHORT_LOOP_EVERY !== SHORT_LOOP_EVERY - 1) return;
      tl.pause();
      fadeRef.current = gsap
        .timeline({
          onComplete: () => {
            fadeRef.current = null;
            nextLoop(tl);
          },
        })
        .to(q(`.${DECISION_BORDER_CLASS}`), {
          stroke: DECISION_NO_COLOR,
          duration: NO_TURN_RED,
          ease: 'none',
        })
        .to(
          q('.svg-draw'),
          { opacity: 0, duration: FADE_OUT, ease: 'none' },
          `+=${NO_HOLD}`,
        )
        .timeScale(PLAYBACK_SPEED);
    };

    // Reset for a fresh loop; clearProps drops the inline red stroke so the
    // border is electric again.
    tl.set(q('.svg-draw'), { drawSVG: '0%', opacity: 1, clearProps: 'stroke' });
    build({ tl, q, gate });
    // Decision "yes" ran through: hold the finished diagram, then fade it out
    // before the loop starts again.
    tl.to(
      q('.svg-draw'),
      { opacity: 0, duration: FADE_OUT, ease: 'none' },
      '+=0.5',
    ).to({}, { duration: 0.2 });
    tl.timeScale(PLAYBACK_SPEED);

    timelineRef.current = tl;
    // Offscreen builds stay parked at frame 0; the observer plays them on
    // entry so the sequence is always seen from the agents card.
    if (isVisibleRef.current) tl.play();
  }, [build, isVisibleRef]);

  // Rebuild when the width changes by more than 20px (debounced), since the
  // DrawSVG percentages are captured from the rendered path lengths. A higher
  // threshold + longer debounce avoids teardown/rebuild churn from minor
  // layout jitter while the user is scrolling on iOS.
  useEffect(() => {
    if (!container.current) return;

    let lastWidth = container.current.offsetWidth;
    let timeoutId: NodeJS.Timeout;

    const disconnect = observeElementResize(container.current, (entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (Math.abs(width - lastWidth) > 20) {
          lastWidth = width;
          clearTimeout(timeoutId);
          timeoutId = setTimeout(restartAnimation, 200);
        }
      }
    });

    return () => {
      disconnect();
      clearTimeout(timeoutId);
    };
  }, [restartAnimation]);

  useGSAP(
    () => {
      // Let the border generators measure their containers first.
      gsap.delayedCall(0.2, restartAnimation);

      return () => {
        timelineRef.current?.kill();
        fadeRef.current?.kill();
      };
    },
    { scope: container },
  );

  return { container, activeButton };
}
